import { Injectable } from '@angular/core';
import { CellColors, LabelValuePair } from '@ddv/models';
import { Theme } from '@hs/ui-core-presentation';

import { ColorInfo } from '../models/color-info';
import { outdatedBarColors } from './color-sets/bars';
import { darkBaseColors, lightBaseColors } from './color-sets/base';
import { darkCellAndFontColors, lightCellAndFontColors } from './color-sets/cell-and-fonts';
import { outdatedCellColors } from './color-sets/cells';
import {
    baseColorConfig,
    darkSolidColorConfig,
    lightSolidColorConfig,
    lineColorConfig,
    mirroredBarAndLineColorConfig,
    monochromeColorConfig,
    multiColorConfig,
} from './color-sets/configs';
import { darkThemeFontColors, lightThemeFontColors, outdatedFontColors } from './color-sets/fonts';
import { newLineChartColorValues } from './color-sets/lines';
import { darkCustomColorMap, lightCustomColorMap, outdatedCustomColorMap } from './color-sets/maps';

@Injectable()
export class ColorMetadataService {
    static mapThemeColors(currentTheme: Theme, columnCondition: CellColors): void {
        ColorMetadataService.mapBarColors(currentTheme, columnCondition);
    }

    static mapOutdatedLineChartColorType(): 'SOLID' {
        return 'SOLID';
    }

    static mapOutdatedLineChartColorName(): string {
        return 'Line Chart Color Fill';
    }

    static mapOutdatedLineChartValueColorName(colorName: string, index: number): string {
        return newLineChartColorValues.indexOf(colorName) === -1 ? (index === 0 ? '#26A9E0' : '#A4C439') : colorName;
    }

    static isLineChartConfigCustomStyleOutdated(colorName: string): boolean {
        return newLineChartColorValues.indexOf(colorName) === -1;
    }

    static mapOutdatedCustomColorValue(colorValue: string): string {
        return outdatedCustomColorMap.get(colorValue) ?? colorValue;
    }

    static mapThemeCustomColorValue(colorValue: string, theme: Theme): string {
        if (theme === Theme.light) {
            return lightCustomColorMap.get(colorValue) ?? colorValue;
        }

        return darkCustomColorMap.get(colorValue) ?? colorValue;
    }

    static mapBarColors(currentTheme: Theme, columnCondition: CellColors): void {
        const colorsLightTheme = ColorMetadataService.getColors(Theme.light);
        const colorsDarkTheme = ColorMetadataService.getColors(Theme.dark);
        const getColor = (colors: LabelValuePair<string>[]): LabelValuePair<string> | undefined => {
            return colors.find((color) => color.value === columnCondition.barColor);
        };
        const colorLightTheme = getColor(colorsLightTheme);
        const colorDarkTheme = getColor(colorsDarkTheme);
        if (currentTheme === Theme.light && !colorLightTheme && !!colorDarkTheme) {
            const correspondingColor = colorsLightTheme.find((color) => color.label === colorDarkTheme.label);
            columnCondition.barColor = correspondingColor?.value;
        }
        if (currentTheme === Theme.dark && !colorDarkTheme && !!colorLightTheme) {
            const correspondingColor = colorsDarkTheme.find((color) => color.label === colorLightTheme.label);
            columnCondition.barColor = correspondingColor?.value;
        }
    }

    static normalizeStackedAreaTransparentColorName(colorName: string): string {
        return `${colorName.substring(0, colorName.indexOf(' Transparent'))}`;
    }

    static makeStackedAreaColorsSemiTransparent(values: string[]): string[] {
        return values.map((value) => ColorMetadataService.makeColorSemiTransparent(value));
    }

    static makeColorSemiTransparent(value: string): string {
        const color = value.substring(1).split('');

        const red = parseInt(`${color[0]}${color[1]}`, 16);
        const green = parseInt(`${color[2]}${color[3]}`, 16);
        const blue = parseInt(`${color[4]}${color[5]}`, 16);
        const alpha = 0.6;

        return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }

    static getOutdatedFontColors(): LabelValuePair<string>[] {
        return [...outdatedFontColors];
    }

    static getOutdatedCellColors(): LabelValuePair<string>[] {
        return [...outdatedCellColors];
    }

    static getOutdatedBarColors(): LabelValuePair<string>[] {
        return [...outdatedBarColors];
    }

    static getFontColors(theme: Theme): LabelValuePair<string>[] {
        if (theme === Theme.light) {
            return [...lightThemeFontColors];
        }

        return [...darkThemeFontColors];
    }

    static getColors(theme: Theme, isBarColor = false): LabelValuePair<string>[] {
        const baseColors = theme === Theme.light ? [...lightBaseColors] : [...darkBaseColors];

        if (!isBarColor) {
            baseColors.push({ label: 'N/A', value: '' });
        }

        return baseColors;
    }

    static getCellAndFontColors(theme: Theme): CellColors[] {
        if (theme === Theme.light) {
            return [...lightCellAndFontColors];
        }

        return [...darkCellAndFontColors];
    }

    getColorConfig(): ColorInfo[] {
        return [...baseColorConfig];
    }

    getMonochromaticColorConfig(): ColorInfo[] {
        return [...monochromeColorConfig];
    }

    getSolidColorConfig(theme: Theme): ColorInfo[] {
        if (theme === Theme.light) {
            return [...lightSolidColorConfig];
        }

        return [...darkSolidColorConfig];
    }

    getMultiColorConfig(): ColorInfo[] {
        return [...multiColorConfig];
    }

    getMirroredBarAndLineChartColorConfig(): ColorInfo[] {
        return [...mirroredBarAndLineColorConfig];
    }

    getLineChartColorOptions(): ColorInfo[] {
        return [...lineColorConfig];
    }

    mapColorToVariable(fontColor: string, backgroundColor: string): string | undefined {
        const color = [...lightCellAndFontColors, ...darkCellAndFontColors].find((c) => {
            return c.fontColor === fontColor && c.cellColor === backgroundColor;
        });

        return color?.var;
    }
}
